import { Entity } from '@backstage/catalog-model';
import _, { capitalize } from 'lodash';
import { serializeEnvironmentParamKey } from './skeleton-entities';

export const INVALID_ENV_PARAMS_MSG =
  'Only one environment parameters section (see `environmentParameters` flag) is allowed';

/**
 * Ensures there is only one parameter section marked as environmentParameters.
 * Throws an error if more than one such section is found.
 *
 * @param template - The entity template to validate.
 */
export function validateEnvironmentParameters(template: Entity): Entity {
  const parameters = template.spec?.parameters;

  if (!Array.isArray(parameters)) return template;

  let found = false;

  for (const param of parameters) {
    if (
      _.isObject(param) &&
      !Array.isArray(param) &&
      param.environmentParameters === true
    ) {
      if (found) throw new Error(INVALID_ENV_PARAMS_MSG);
      found = true;
    }
  }

  return template;
}

function collectRootPropertyNames(schema: any): Set<string> {
  return new Set(Object.keys(schema?.properties || {}));
}

function serializeSchema(
  schema: any,
  environmentName: string,
  rootPropertyNames: Set<string>,
  inRoot: boolean = false,
): any {
  if (
    typeof schema === 'boolean' ||
    schema === null ||
    typeof schema !== 'object'
  )
    return schema;

  if (Array.isArray(schema)) {
    return schema.map(s =>
      serializeSchema(s, environmentName, rootPropertyNames, inRoot),
    );
  }

  const result: any = {};

  for (const [key, value] of Object.entries(schema)) {
    if (
      key === 'properties' &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      const newProps: Record<string, any> = {};

      for (const [propKey, propSchema] of Object.entries(value)) {
        let newKey = propKey;
        const shouldSerializeKey = inRoot || rootPropertyNames.has(propKey);

        if (shouldSerializeKey) {
          newKey = serializeEnvironmentParamKey(environmentName, propKey);
        }

        const serializedProp = serializeSchema(
          propSchema,
          environmentName,
          rootPropertyNames,
          shouldSerializeKey,
        );

        // if no title is present, set it to the original property key
        if (
          shouldSerializeKey &&
          typeof serializedProp === 'object' &&
          serializedProp !== null
        ) {
          if (!('title' in serializedProp)) {
            serializedProp.title = capitalize(propKey);
          }
        }

        newProps[newKey] = serializedProp;
      }

      result.properties = newProps;
      continue;
    }

    if (key === 'required' && Array.isArray(value)) {
      result.required = value.map((reqKey: string) =>
        inRoot || rootPropertyNames.has(reqKey)
          ? serializeEnvironmentParamKey(environmentName, reqKey)
          : reqKey,
      );
      continue;
    }

    // Recursively process other keys (allof, anyof, oneof, etc.)
    if (Array.isArray(value)) {
      result[key] = value.map(item =>
        serializeSchema(item, environmentName, rootPropertyNames, inRoot),
      );
    } else if (typeof value === 'object' && value !== null) {
      result[key] = serializeSchema(
        value,
        environmentName,
        rootPropertyNames,
        inRoot,
      );
    } else {
      result[key] = value;
    }
  }

  return result;
}

const createEnvironmentStep = (baseStep: any, environmentName: string): any => {
  const schema = _.omit(baseStep, 'environmentParameters');

  const rootPropertyNames = collectRootPropertyNames(schema);
  const serializedSchema = serializeSchema(
    schema,
    environmentName,
    rootPropertyNames,
  );

  const res = {
    ...baseStep,
    ...serializedSchema,
    stepEnvironment: environmentName,
    title: `${capitalize(environmentName)} variables`,
  };

  return res;
};

export const processSteps = (template: Entity, environments: string[]) => {
  const steps = template.spec?.parameters;

  if (!Array.isArray(steps)) return steps;

  return steps.flatMap(step => {
    if (
      _.isObject(step) &&
      !Array.isArray(step) &&
      step.environmentParameters === true
    ) {
      return environments.map(envName => createEnvironmentStep(step, envName));
    }
    return [step];
  });
};
